// Shared handling for the server-side subscription gate (HTTP 402).
//
// With ENTITLEMENT_ENFORCE on (STUD-461), any gated API call can answer 402 with a stable
// body: { error: 'subscription_required', message: '...' } -- most often because a trial
// ended or was cancelled while the user was still logged in.
//
// Every 402 goes through dispatchSubscriptionRequired (STUD-631). The axios response
// interceptor (logic/axiosSetup.js) calls it first, so no screen can miss one, and then
// withholds the error from the caller so no error alert flashes up before the page leaves.
// handleSubscriptionRequired is kept for call sites that still check for themselves; it
// delegates here too, and the dispatcher is idempotent, so the two never double-redirect.
//
// Where it sends the user depends on who they are: a parent or teacher goes to checkout,
// but a student cannot pay, so they get /subscription-required ("ask your parent").

import { getActiveCourseId } from './courseContext';

export const SUBSCRIPTION_REQUIRED_CODE = 'subscription_required';
export const SUBSCRIPTION_REQUIRED_ROUTE = '/subscription-required';
const CHECKOUT_ROUTE = '/checkout';
const ROLE_STORAGE_KEY = 'studyapp_role';

/**
 * True when an axios error is the server-side "subscription required" 402 gate.
 * Matches on HTTP status primarily, with the stable body token as a secondary signal.
 * @param {*} error - an axios error
 * @returns {boolean}
 */
export function isSubscriptionRequiredError(error) {
  const response = error && error.response;
  if (!response) {
    return false;
  }
  return response.status === 402 || response.data?.error === SUBSCRIPTION_REQUIRED_CODE;
}

function readStoredRole() {
  try {
    return window.localStorage.getItem(ROLE_STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

/**
 * Where a blocked user should go.
 * @param {{ role?: string|null, courseId?: string|null }} [options]
 * @returns {string}
 */
export function subscriptionRequiredTarget({ role, courseId } = {}) {
  if (role === 'student') {
    return SUBSCRIPTION_REQUIRED_ROUTE;
  }
  return courseId
    ? `${CHECKOUT_ROUTE}?courseId=${encodeURIComponent(courseId)}`
    : CHECKOUT_ROUTE;
}

// Set once a full-page redirect has been issued. The page is about to unload, so nothing
// may redirect again -- a dashboard firing ten calls at once must leave exactly once.
let hardRedirectIssued = false;

/** True once a full-page redirect for a 402 is under way. */
export function isSubscriptionRedirectPending() {
  return hardRedirectIssued;
}

/** Test-only: forget a previous hard redirect. */
export function resetSubscriptionGateForTests() {
  hardRedirectIssued = false;
}

/**
 * The single place that routes a blocked user. Idempotent: a no-op once a hard redirect is
 * in flight, and a no-op when already on the target page (so the target page's own
 * background calls cannot loop it).
 * @param {object} [options]
 * @param {string} [options.courseId] - the course to buy; defaults to the active course
 * @param {string} [options.role] - defaults to the signed-in role from localStorage
 * @param {(to: string) => void} [options.navigate] - react-router navigate; when given the
 *   move stays inside the app, otherwise it is a full page load
 * @returns {boolean} whether a redirect was issued
 */
export function dispatchSubscriptionRequired({ courseId, role, navigate } = {}) {
  if (hardRedirectIssued) {
    return false;
  }
  const resolvedRole = role !== undefined ? role : readStoredRole();
  const resolvedCourseId = courseId || (resolvedRole === 'student' ? null : getActiveCourseId());
  const target = subscriptionRequiredTarget({ role: resolvedRole, courseId: resolvedCourseId });
  const targetPath = target.split('?')[0];

  if (window.location.pathname === targetPath) {
    return false;
  }

  if (typeof navigate === 'function') {
    navigate(target);
  } else {
    hardRedirectIssued = true;
    window.location.assign(target);
  }
  return true;
}

/**
 * If `error` is the subscription gate, route the user once and return true so the caller
 * can `return` early and skip its generic error/retry/polling path. Otherwise returns false
 * and does nothing, letting the caller handle the error normally.
 * @param {*} error - an axios error
 * @param {(to: string) => void} navigate - react-router navigate function
 * @param {{ courseId?: string }} [options]
 * @returns {boolean} whether the subscription gate was handled
 */
export function handleSubscriptionRequired(error, navigate, options = {}) {
  if (!isSubscriptionRequiredError(error)) {
    return false;
  }
  // Already routed (by the interceptor or an earlier call): still "handled", so the caller
  // skips its error path.
  dispatchSubscriptionRequired({ courseId: options.courseId, navigate });
  return true;
}
